import React, { useEffect, useState } from "react";
import {
    Box, Paper, Grid, FormControl, InputLabel,
    Select, MenuItem, TextField, IconButton,
    Button, Autocomplete, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getProductsByCatalog, getSkusByCatalog } from "../../../controllers/CatalogController";

export default function OutcomeBlock({
    block,
    blockIndex,
    blocksLength,
    form,
    enums,
    categories,
    locations,
    attributeConfig,
    products,
    skus,
    onUpdate,
    onRemoveOutcomeBlock,
    onAddCondition,
    onRemoveCondition,
    onUpdateLogic,
    selectedCategory,
    selectedProduct,
    selectedSku
}) {
    const DISCOUNT_TYPES = Object.keys(enums.DiscountType);
    const [productOptions, setProductOptions] = useState(selectedProduct ? Array.isArray(selectedProduct) ? selectedProduct : [selectedProduct] : products);
    const [skuOptions, setSkuOptions] = useState(selectedSku ? Array.isArray(selectedSku) ? selectedSku : [selectedSku] : skus);
    const [selectedBlockCategory, setSelectedBlockCategory] = useState(selectedCategory ? selectedCategory : null);
    const [selectedBlockProduct, setSelectedBlockProduct] = useState(selectedProduct ? selectedProduct : null);

    const fetchProducts = async () => {
        if (!(form && form.catalogId)) return;
        if (selectedProduct) return;
        try {
            const res = await getProductsByCatalog({
                catalogId: form.catalogId,
                categoryId: selectedBlockCategory ? Array.isArray(selectedBlockCategory) ? selectedBlockCategory.map(c => c.id ?? c) : selectedBlockCategory.id ?? selectedBlockCategory : ""
            });
            setProductOptions(res);
        } catch (err) {
            console.log(err);
        }
    };

    const fetchSkus = async () => {
        if (!(form && form.catalogId && selectedSku)) return;
        if (selectedSku) return;
        try {
            const res = await getSkusByCatalog({
                catalogId: form.catalogId,
                categoryId: selectedBlockCategory ? Array.isArray(selectedBlockCategory) ? selectedBlockCategory.map(c => c.id ?? c) : selectedBlockCategory.id ?? selectedBlockCategory : "",
                productId: selectedBlockProduct ? Array.isArray(selectedBlockProduct) ? selectedBlockProduct.map(p => p.id ?? p) : selectedBlockProduct.id ?? selectedBlockProduct : ""
            });
            setSkuOptions(res);
        } catch (err) {
            console.log(err);
        }
    };

    useEffect(() => {
        fetchProducts();
        fetchSkus();
    }, [selectedBlockCategory])

    useEffect(() => {
        fetchSkus();
    }, [selectedBlockProduct])



    const updateCondition = (conditionId, key, value) => {
        const updatedWhen = block.when.map((c, ind) => {
            if (ind !== conditionId) return c;

            if (key === "attributeType") {
                return {
                    ...c,
                    attributeType: value,
                    attributeFilterType: "",
                    attributeFilterClause: "",
                    attributeFilterValue: "",
                };
            }
            if (key === "attributeFilterType") {
                return {
                    ...c,
                    attributeFilterType: value,
                    attributeFilterClause: "",
                    attributeFilterValue: ""
                };
            }
            if (key === "attributeFilterClause") {
                return {
                    ...c,
                    attributeFilterClause: value,
                    attributeFilterValue: ""
                };
            }
            if (key === "attributeFilterValue") {
                // trigger API calls on category/product selection
                if (c.attributeType === "CATEGORY") {
                    setSelectedBlockCategory(value);
                }
                if (c.attributeType === "PRODUCT") {
                    setSelectedBlockProduct(value);
                }
                if (Array.isArray(value)) {
                    return { ...c, attributeFilterValue: value };
                }

                if (typeof value === "object" && value !== null) {
                    return { ...c, attributeFilterValue: value };
                }

                return { ...c, attributeFilterValue: value };
            }

            return { ...c, [key]: value };
        });
        onUpdate(blockIndex, { ...block, when: updatedWhen });
    };

    const updateOutcome = (key, value) => {
        if (key === "discountPercentage" && (value > 100 || value < 0)) return;
        onUpdate(blockIndex, {
            ...block,
            then: {
                discount: { ...block.then.discount, [key]: value },
            },
        });
    };

    const getFieldsFromConfig = (attributeType) => {
        if (!attributeType) return [];
        const config = attributeConfig.find((a) => a.attributeType === attributeType);
        return config?.fields || [];
    };

    const getOperatorsFromConfig = (row) => {
        if (!row.attributeType || !row.attributeFilterType) return [];
        const typeConfig = attributeConfig.find((a) => a.attributeType === row.attributeType);
        const fieldConfig = typeConfig?.fields.find((f) => f.name === row.attributeFilterType);
        return fieldConfig?.operators || [];
    };

    const getFilteredAttributes = () => {
        let attrs = attributeConfig?.map((a) => a) || [];
        if (selectedSku || selectedSku?.length > 0) {
            attrs = attrs.filter((a) => !["CATEGORY", "PRODUCT"].includes(a.attributeType));
        } else if (selectedProduct || selectedProduct?.length > 0) {
            attrs = attrs.filter((a) => a.attributeType !== "CATEGORY");
        }
        return attrs;
    };

    const resolveOptions = (row) => {
        if (!row.attributeType || !row.attributeFilterType) return [];

        if (row.attributeType === "CONTEXT") {
            if (row.attributeFilterType === "storeIds") {
                const stores = locations?.stores || [];
                const rawStoreIds = Array.isArray(form?.storeIds) ? form.storeIds : [];
                const selectedStoreIds = new Set(
                    rawStoreIds.map((item) => {
                        if (item == null) return null;
                        if (typeof item === "string" || typeof item === "number") return String(item);
                        if (typeof item === "object") return String(item.id ?? item.storeId ?? item.value ?? "");
                        return null;
                    }).filter(Boolean)
                );
                const filteredStores = selectedStoreIds.size
                    ? stores.filter((store) => selectedStoreIds.has(String(store.storeId)))
                    : stores;
                return filteredStores.map((store) => ({ name: store.storeName, id: store.storeId }));
            }
            if (row.attributeFilterType === "geoTagIds") {
                return Array.isArray(form?.geoTagIds) && form.geoTagIds.length > 0
                    ? form.geoTagIds.map((tag) => Number(tag))
                    : locations?.geoTags || [];
            }
            if (row.attributeFilterType === "customerGroups") {
                const allGroups = locations?.customerGroups || [];
                const existing = form?.when?.find(
                    (w) => w.attributeType === "CONTEXT" && w.attributeFilterType === "customerGroups"
                )?.attributeFilterValue;
                const normalizedExisting = Array.isArray(existing)
                    ? existing.map((item) => {
                        if (!item) return null;
                        if (typeof item === "object") return item;
                        const match = allGroups.find((g) => String(g.id) === String(item));
                        return match || { id: item, name: "" };
                    }).filter(Boolean)
                    : [];
                return normalizedExisting.length > 0 ? normalizedExisting : allGroups;
            }
            if (row.attributeFilterType === "customerType") return enums.CustomerType || [];
            if (row.attributeFilterType === "appSource") return locations?.requestSources || [];
        }

        // CATEGORY → shared from rule association
        if (row.attributeType === "CATEGORY") {
            return selectedCategory
                ? Array.isArray(selectedCategory) ? selectedCategory : [selectedCategory]
                : categories;
        }

        // PRODUCT → this block's local options (fetched after category selected)
        if (row.attributeType === "PRODUCT") {
            return productOptions;
        }

        // // SKU → this block's local options
        if (row.attributeType === "SKU") {
            return skuOptions;
        }

        return [];
    };

    const getOptionLabelFromConfig = (row, option) => {
        if (!option) return "";
        if (typeof option === "string" || typeof option === "number") return String(option);
        if (row.attributeFilterType === "storeIds" || row.attributeFilterType === "customerGroups") {
            return `${option.id ?? ""} - ${option.name ?? ""}`.trim();
        }
        return option?.[row.attributeFilterType] != null
            ? row.attributeFilterType === "name"
                ? String(option[row.attributeFilterType])
                : `${option[row.attributeFilterType]}-${option?.name ?? ""}`
            : "";
    };

    const getOptionIdentityFromConfig = (row, option) => {
        if (option == null) return null;
        if (typeof option === "string" || typeof option === "number") return option;
        if (row.attributeFilterType === "storeIds") return option.id ?? option.storeId ?? null;
        if (row.attributeType === "SKU") return option.productItemId ?? option.id ?? option.skuId ?? null;
        if (row.attributeType === "PRODUCT") return option.id ?? option.productId ?? null;
        if (row.attributeType === "CATEGORY") return option.id ?? option.categoryId ?? null;
        return option[row.attributeFilterType] ?? option.id ?? null;
    };

    const getOptionNameFromConfig = (row, option) => {
        if (!option || typeof option !== "object") return "";
        if (row.attributeFilterType === "storeIds") return option.name ?? option.storeName ?? "";
        return option.name ?? "";
    };

    const resolveAutocompleteValueFromConfig = (row, options, rawValue) => {
        if (rawValue == null || rawValue === "") return null;
        if (typeof rawValue === "number") return rawValue;
        if (typeof rawValue === "object") {
            const rawIdentity = getOptionIdentityFromConfig(row, rawValue);
            if (rawIdentity != null) {
                const match = (options || []).find(
                    (o) => String(getOptionIdentityFromConfig(row, o)) === String(rawIdentity)
                );
                if (match) return match;
            }
            return rawValue;
        }
        return (options || []).find((o) => {
            const id = getOptionIdentityFromConfig(row, o);
            const name = getOptionNameFromConfig(row, o);
            const keyed = o && typeof o === "object" ? o?.[row.attributeFilterType] : o;
            return (
                (id != null && String(id) === String(rawValue)) ||
                (keyed != null && String(keyed) === String(rawValue)) ||
                (name && String(name) === String(rawValue))
            );
        }) ?? rawValue;
    };

    const shouldUseAutocomplete = (row, options) => {
        if (!row?.attributeFilterType) return false;
        if (!Array.isArray(options) || options.length === 0) return false;
        if (row.attributeType === "CONTEXT") return true;
        const firstOption = options[0];
        return (
            typeof firstOption === "object" &&
            row.attributeFilterType in firstOption &&
            firstOption[row.attributeFilterType] != null
        );
    };
    return (
        <Paper
            elevation={0}
            sx={{ border: "1px solid #d9d9d9", borderRadius: 3, p: 2, mb: 3 }}
        >
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography sx={{ fontWeight: 900 }}>
                    Rule Outcome #{blockIndex + 1}
                </Typography>
                {blocksLength > 1 && (
                    <Button
                        color="error"
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => onRemoveOutcomeBlock(blockIndex)}
                        sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                        Remove Outcome
                    </Button>
                )}
            </Box>

            {/* WHEN */}
            <Typography sx={{ color: "text.secondary", mb: 1 }}>
                Match all conditions below to trigger this outcome
            </Typography>

            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 3, p: 2, mb: 2, background: "#fafafa" }}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => onAddCondition(blockIndex)}
                        sx={{ borderRadius: 2, textTransform: "none" }}
                    >
                        Add Condition
                    </Button>
                </Box>

                {block.when.map((row, index) => {
                    const attributeTypes = getFilteredAttributes();
                    const attributes = getFieldsFromConfig(row.attributeType);
                    const operators = getOperatorsFromConfig(row);
                    const OPTIONS = resolveOptions(row);
                    const useAuto = shouldUseAutocomplete(row, OPTIONS);
                    const isMulti = row.attributeFilterClause === "IN";
                    const selectedValueCount = isMulti && Array.isArray(row.attributeFilterValue)
                        ? row.attributeFilterValue.filter((v) => v !== null && v !== undefined && `${v}`.trim() !== "").length
                        : 0;
                    const resolvedValue = isMulti
                        ? (Array.isArray(row.attributeFilterValue)
                            ? row.attributeFilterValue
                                .map((item) => resolveAutocompleteValueFromConfig(row, OPTIONS, item))
                                .filter((item) => item != null)
                            : [])
                        : resolveAutocompleteValueFromConfig(row, OPTIONS, row.attributeFilterValue);
                    const showInError = isMulti && selectedValueCount > 0 && selectedValueCount < 2 && operators.length > 1;
                    const noOptionWarning = row.attributeType && row.attributeFilterType && OPTIONS.length === 0;
                    return (
                        <Box key={index}>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item size={{ xs: 4, md: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Attribute Type</InputLabel>
                                        <Select
                                            label="Attribute Type"
                                            value={row.attributeType}
                                            onChange={(e) => updateCondition(index, "attributeType", e.target.value)}
                                        >
                                            {attributeTypes.map((op) => (
                                                <MenuItem key={op.attributeType} value={op.attributeType}>{op.label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item size={{ xs: 6, md: 3 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Attribute</InputLabel>
                                        <Select
                                            label="Attribute"
                                            value={row.attributeFilterType}
                                            onChange={(e) => updateCondition(index, "attributeFilterType", e.target.value)}
                                        >
                                            {attributes.map((field) => (
                                                <MenuItem key={field.name} value={field.name}>{field.name}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item size={{ xs: 5, md: 2 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Operator</InputLabel>
                                        <Select
                                            label="Operator"
                                            value={row.attributeFilterClause || ""}
                                            onChange={(e) => updateCondition(index, "attributeFilterClause", e.target.value)}
                                        >
                                            {operators.map((op) => (
                                                <MenuItem key={op} value={op}>{op}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item size={{ xs: 6, md: 4 }}>
                                    <FormControl fullWidth size="small">
                                        {useAuto ? (
                                            <Autocomplete
                                                multiple={isMulti}
                                                options={OPTIONS || []}
                                                value={isMulti ? resolvedValue : resolvedValue || null}
                                                onChange={(_, val) => updateCondition(index, "attributeFilterValue", val)}
                                                getOptionLabel={(option) => getOptionLabelFromConfig(row, option)}
                                                isOptionEqualToValue={(option, val) => {
                                                    if (typeof option === "number" && typeof val === "number") return option === val;
                                                    const a = getOptionIdentityFromConfig(row, option);
                                                    const b = getOptionIdentityFromConfig(row, val);
                                                    if (a != null && b != null) return String(a) === String(b);
                                                    return String(getOptionLabelFromConfig(row, option)) === String(getOptionLabelFromConfig(row, val));
                                                }}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        label="Select Value"
                                                        size="small"
                                                        error={showInError}
                                                        helperText={showInError ? "Select at least 2 values for IN operator" : ""}
                                                    />
                                                )}
                                            />
                                        ) : (
                                            <TextField
                                                size="small"
                                                label="Value"
                                                fullWidth
                                                value={row.attributeFilterValue || ""}
                                                onChange={(e) => updateCondition(index, "attributeFilterValue", e.target.value)}
                                                error={noOptionWarning}
                                                helperText={noOptionWarning ? "No options available" : ""}
                                            />
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item size={{ xs: 3, md: 1 }}>
                                    <IconButton
                                        onClick={() => onRemoveCondition(blockIndex, index)}
                                        sx={{ border: "1px solid #ff4d4f", color: "#ff4d4f", borderRadius: 2, width: 38, height: 38 }}
                                    >
                                        <DeleteOutlineIcon />
                                    </IconButton>
                                </Grid>
                            </Grid>

                            {index < block.when.length - 1 && (
                                <Box sx={{ mt: 1.2, mb: 2 }}>
                                    <FormControl size="small" sx={{ width: 120 }}>
                                        <Select
                                            value={row.nextOperator ?? "AND"}
                                            onChange={(e) => onUpdateLogic(blockIndex, index, e.target.value)}
                                            sx={{ borderRadius: 2, fontWeight: 800 }}
                                        >
                                            <MenuItem value="AND">AND</MenuItem>
                                            <MenuItem value="OR">OR</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Paper>

            {/* THEN */}
            <Typography sx={{ color: "text.secondary", mb: 1 }}>
                Apply the following discount when conditions match
            </Typography>

            <Paper elevation={0} sx={{ border: "1px solid #e0e0e0", borderRadius: 3, p: 2 }}>
                <Grid container spacing={2}>
                    <Grid item size={{ xs: 6, md: 4 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Discount Type</InputLabel>
                            <Select
                                label="Discount Type"
                                value={block.then.discount.discountType}
                                onChange={(e) => updateOutcome("discountType", e.target.value)}
                            >
                                {DISCOUNT_TYPES.map((d) => (
                                    <MenuItem key={d} value={d}>{d}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item size={{ xs: 6, md: 4 }}>
                        <TextField
                            size="small"
                            label="Discount %"
                            type="number"
                            fullWidth
                            required
                            value={block.then.discount.discountPercentage}
                            onChange={(e) => updateOutcome("discountPercentage", Number(e.target.value))}
                            error={!block.then.discount.discountPercentage || Number(block.then.discount.discountPercentage) <= 0 || Number(block.then.discount.discountPercentage) > 30}
                            helperText="Discount is required"
                        />
                    </Grid>
                </Grid>
            </Paper>
        </Paper>
    );
}